import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FileCheck,
  Upload,
  Download,
  Trash2,
  Calendar,
  Search,
  Filter,
  CheckCircle2,
  AlertCircle,
  FolderArchive,
  UserCheck,
} from "lucide-react";

export default function BoletosOrnatoPage() {
  const { user } = useAuth();
  const isHRorAdmin = user?.rol === "administrador" || user?.rol === "rrhh";

  const [myBoletos, setMyBoletos] = useState([]);
  const [allBoletos, setAllBoletos] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filtros
  const [filterAnio, setFilterAnio] = useState("todos");
  const [search, setSearch] = useState("");

  // Carga Masiva
  const currentYear = new Date().getFullYear();
  const [masivoAnio, setMasivoAnio] = useState(currentYear.toString());
  const [masivoFile, setMasivoFile] = useState(null);
  const [uploadingMasivo, setUploadingMasivo] = useState(false);
  const [masivoResult, setMasivoResult] = useState(null);

  // Carga Individual
  const [individualEmpId, setIndividualEmpId] = useState("");
  const [individualAnio, setIndividualAnio] = useState(currentYear.toString());
  const [individualFile, setIndividualFile] = useState(null);
  const [uploadingIndividual, setUploadingIndividual] = useState(false);

  const loadData = async () => {
    setLoading(true);
    try {
      const myRes = await api.getMisBoletos();
      setMyBoletos(myRes.data || []);

      if (isHRorAdmin) {
        const [allRes, empRes] = await Promise.all([
          api.getAllBoletos(),
          api.getEmployees(),
        ]);
        setAllBoletos(allRes.data || []);
        setEmployees(empRes.data || []);
      }
    } catch (err) {
      toast.error("Error al cargar boletos de ornato");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [isHRorAdmin]);

  const handleDownload = async (id, anio) => {
    try {
      const res = await api.downloadBoleto(id);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Boleto_Ornato_${anio}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success("Descarga iniciada");
    } catch (err) {
      toast.error("Error al descargar boleto de ornato");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("¿Estás seguro de eliminar este boleto de ornato?")) return;
    try {
      await api.deleteBoleto(id);
      toast.success("Boleto eliminado exitosamente");
      loadData();
    } catch (err) {
      toast.error("Error al eliminar boleto");
    }
  };

  const handleUploadMasivo = async (e) => {
    e.preventDefault();
    if (!masivoFile) {
      toast.error("Selecciona un archivo .zip o .pdf");
      return;
    }
    setUploadingMasivo(true);
    setMasivoResult(null);
    try {
      const formData = new FormData();
      formData.append("anio", masivoAnio);
      formData.append("archivo", masivoFile);

      const res = await api.uploadBoletosMasivo(formData);
      toast.success(res.data.message || "Carga masiva procesada");
      setMasivoResult(res.data);
      setMasivoFile(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error en carga masiva");
    } finally {
      setUploadingMasivo(false);
    }
  };

  const handleUploadIndividual = async (e) => {
    e.preventDefault();
    if (!individualEmpId || !individualFile) {
      toast.error("Completa todos los campos");
      return;
    }
    setUploadingIndividual(true);
    try {
      const formData = new FormData();
      formData.append("empleado_id", individualEmpId);
      formData.append("anio", individualAnio);
      formData.append("archivo", individualFile);

      const res = await api.uploadBoletoIndividual(formData);
      toast.success(res.data.message || "Boleto asignado exitosamente");
      setIndividualFile(null);
      setIndividualEmpId("");
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al asignar boleto");
    } finally {
      setUploadingIndividual(false);
    }
  };

  const filteredAllBoletos = allBoletos.filter((b) => {
    const matchesSearch =
      b.empleado_nombre?.toLowerCase().includes(search.toLowerCase()) ||
      b.dpi?.includes(search) ||
      b.empresa?.toLowerCase().includes(search.toLowerCase());
    const matchesAnio = filterAnio === "todos" || b.anio.toString() === filterAnio;
    return matchesSearch && matchesAnio;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2.5">
            <div className="p-2 bg-emerald-100 rounded-lg text-emerald-600">
              <FileCheck className="w-6 h-6" />
            </div>
            Boletos de Ornato
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gestión y descarga digital del Boleto de Ornato para colaboradores
          </p>
        </div>
      </div>

      <Tabs defaultValue="mis-boletos" className="space-y-6">
        <TabsList className="bg-slate-100 p-1 rounded-xl">
          <TabsTrigger value="mis-boletos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
            Mis Boletos ({myBoletos.length})
          </TabsTrigger>
          {isHRorAdmin && (
            <>
              <TabsTrigger value="carga-masiva" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Carga Masiva (ZIP)
              </TabsTrigger>
              <TabsTrigger value="carga-individual" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Asignación Individual
              </TabsTrigger>
              <TabsTrigger value="todos-boletos" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                Registro General ({allBoletos.length})
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Tab: Mis Boletos */}
        <TabsContent value="mis-boletos" className="space-y-4">
          {loading ? (
            <div className="text-center py-12 text-slate-500">Cargando tus boletos...</div>
          ) : myBoletos.length === 0 ? (
            <Card className="border-dashed">
              <CardContent className="py-12 text-center space-y-3">
                <FileCheck className="w-12 h-12 text-slate-300 mx-auto" />
                <h3 className="font-semibold text-slate-700 text-lg">No tienes boletos de ornato disponibles</h3>
                <p className="text-sm text-slate-500 max-w-sm mx-auto">
                  El departamento de RRHH cargará tu constancia del Boleto de Ornato en cuanto esté disponible.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
              {myBoletos.map((b) => (
                <Card key={b.id} className="border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                  <div className="h-2 bg-emerald-500 w-full" />
                  <CardHeader className="pb-3">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wider text-emerald-600 bg-emerald-50 px-2.5 py-1 rounded-full border border-emerald-200">
                        Vigente
                      </span>
                      <span className="text-xs text-slate-400">Subido: {b.fecha_subida || "-"}</span>
                    </div>
                    <CardTitle className="text-2xl font-extrabold text-slate-800 pt-2">
                      Boleto {b.anio}
                    </CardTitle>
                    <CardDescription className="text-xs text-slate-500">
                      Archivo: {b.nombre_archivo}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    <Button
                      onClick={() => handleDownload(b.id, b.anio)}
                      className="w-full bg-emerald-600 hover:bg-emerald-700 text-white flex items-center justify-center gap-2 shadow-sm"
                    >
                      <Download className="w-4 h-4" />
                      Descargar Boleto PDF
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Tab: Carga Masiva (RRHH) */}
        {isHRorAdmin && (
          <TabsContent value="carga-masiva" className="space-y-6">
            <Card className="border-slate-200 shadow-sm max-w-2xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <FolderArchive className="w-5 h-5 text-emerald-600" />
                  Carga Masiva de Boletos de Ornato
                </CardTitle>
                <CardDescription>
                  Sube un archivo comprimido <strong>.ZIP</strong> que contenga los PDFs de todos los colaboradores. El
                  sistema asociará automáticamente cada PDF al colaborador buscando su número de <strong>DPI (13 dígitos)</strong>{" "}
                  en el nombre del archivo (ejemplo: <code>2558754950101.pdf</code> o <code>2558754950101_ornato.pdf</code>).
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUploadMasivo} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="masivo_anio">Año Fiscal del Boleto *</Label>
                    <Select value={masivoAnio} onValueChange={setMasivoAnio}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
                          <SelectItem key={y} value={y.toString()}>
                            Año {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="zip_file">Archivo (.zip con PDFs o .pdf individual) *</Label>
                    <div className="border-2 border-dashed border-slate-200 rounded-xl p-6 text-center hover:border-emerald-500 transition-colors bg-slate-50">
                      <Upload className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                      <Input
                        id="zip_file"
                        type="file"
                        accept=".zip,.pdf"
                        onChange={(e) => setMasivoFile(e.target.files?.[0] || null)}
                        className="max-w-xs mx-auto text-xs bg-white"
                        required
                      />
                      <p className="text-xs text-slate-400 mt-2">Formatos aceptados: .zip con múltiples PDFs o .pdf individual</p>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={uploadingMasivo}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                  >
                    {uploadingMasivo ? "Procesando y Asociando Archivos..." : "Iniciar Carga Masiva"}
                  </Button>
                </form>

                {/* Resultado de la carga masiva */}
                {masivoResult && (
                  <div className="mt-6 p-4 rounded-xl border border-slate-200 bg-slate-50 space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="font-semibold text-slate-800 text-sm">Resumen de Procesamiento</h4>
                      <div className="flex gap-2">
                        <Badge className="bg-emerald-100 text-emerald-800 border-emerald-300">
                          {masivoResult.total_exitosos} Exitosos
                        </Badge>
                        <Badge className="bg-rose-100 text-rose-800 border-rose-300">
                          {masivoResult.total_fallidos} No Asociados
                        </Badge>
                      </div>
                    </div>

                    {masivoResult.exitosos?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-slate-600">Asociados con éxito:</p>
                        <div className="max-h-36 overflow-y-auto space-y-1 text-xs">
                          {masivoResult.exitosos.map((item, idx) => (
                            <div key={idx} className="flex justify-between p-1.5 bg-white rounded border border-slate-200">
                              <span className="font-medium text-slate-700">{item.empleado}</span>
                              <span className="text-slate-400">DPI: {item.dpi}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {masivoResult.fallidos?.length > 0 && (
                      <div className="space-y-2">
                        <p className="text-xs font-semibold text-rose-600">Archivos sin coincidencia de DPI:</p>
                        <div className="max-h-36 overflow-y-auto space-y-1 text-xs">
                          {masivoResult.fallidos.map((item, idx) => (
                            <div key={idx} className="flex justify-between p-1.5 bg-rose-50 rounded border border-rose-200 text-rose-700">
                              <span>{item.archivo}</span>
                              <span className="text-xs">{item.motivo}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Tab: Asignación Individual (RRHH) */}
        {isHRorAdmin && (
          <TabsContent value="carga-individual" className="space-y-6">
            <Card className="border-slate-200 shadow-sm max-w-xl mx-auto">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-slate-800">
                  <UserCheck className="w-5 h-5 text-emerald-600" />
                  Asignación Individual de Boleto
                </CardTitle>
                <CardDescription>Sube el archivo PDF para un colaborador específico.</CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleUploadIndividual} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="ind_emp">Colaborador *</Label>
                    <Select value={individualEmpId} onValueChange={setIndividualEmpId}>
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Seleccionar colaborador..." />
                      </SelectTrigger>
                      <SelectContent className="max-h-60">
                        {employees.map((e) => (
                          <SelectItem key={e.id} value={e.id}>
                            {e.nombre} {e.apellido} (DPI: {e.dpi})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ind_anio">Año Fiscal *</Label>
                    <Select value={individualAnio} onValueChange={setIndividualAnio}>
                      <SelectTrigger className="w-full">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
                          <SelectItem key={y} value={y.toString()}>
                            Año {y}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="ind_pdf">Archivo PDF del Boleto *</Label>
                    <Input
                      id="ind_pdf"
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setIndividualFile(e.target.files?.[0] || null)}
                      required
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={uploadingIndividual}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                  >
                    {uploadingIndividual ? "Guardando..." : "Asignar Boleto al Colaborador"}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Tab: Registro General (RRHH) */}
        {isHRorAdmin && (
          <TabsContent value="todos-boletos" className="space-y-4">
            <div className="flex flex-col sm:flex-row gap-3 items-center justify-between bg-white p-4 rounded-xl border border-slate-200 shadow-sm">
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
                <Input
                  placeholder="Buscar colaborador, DPI..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="flex items-center gap-2 w-full sm:w-auto">
                <Filter className="w-4 h-4 text-slate-400" />
                <Select value={filterAnio} onValueChange={setFilterAnio}>
                  <SelectTrigger className="w-[160px]">
                    <SelectValue placeholder="Año" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="todos">Todos los Años</SelectItem>
                    {[currentYear + 1, currentYear, currentYear - 1, currentYear - 2].map((y) => (
                      <SelectItem key={y} value={y.toString()}>
                        Año {y}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm">
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                  <thead className="bg-slate-50 text-slate-600 border-b border-slate-200 font-medium">
                    <tr>
                      <th className="px-4 py-3">Colaborador</th>
                      <th className="px-4 py-3">Año</th>
                      <th className="px-4 py-3">Empresa y Área</th>
                      <th className="px-4 py-3">Archivo</th>
                      <th className="px-4 py-3">Fecha Subida</th>
                      <th className="px-4 py-3 text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredAllBoletos.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-slate-500">
                          No se encontraron boletos registrados con los filtros seleccionados
                        </td>
                      </tr>
                    ) : (
                      filteredAllBoletos.map((b) => (
                        <tr key={b.id} className="hover:bg-slate-50/80 transition-colors">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-slate-800">{b.empleado_nombre}</div>
                            <div className="text-xs text-slate-400">DPI: {b.dpi}</div>
                          </td>
                          <td className="px-4 py-3">
                            <Badge className="bg-emerald-50 text-emerald-800 border-emerald-300 font-bold">
                              {b.anio}
                            </Badge>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-600">
                            <div>{b.empresa || "CPS"}</div>
                            <div className="text-slate-400">{b.departamento}</div>
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500 max-w-xs truncate">
                            {b.nombre_archivo}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">
                            {b.fecha_subida || "-"}
                          </td>
                          <td className="px-4 py-3 text-right space-x-1">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleDownload(b.id, b.anio)}
                              className="border-slate-200 hover:bg-slate-100 text-xs"
                            >
                              <Download className="w-3.5 h-3.5 mr-1 text-slate-600" />
                              PDF
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleDelete(b.id)}
                              className="text-rose-600 hover:bg-rose-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
